import { copyLabel, useCopyState } from '../components/copy'
import { radius } from '../components/primitives/shape'

/**
 * The line that puts the stagepost command on a machine.
 *
 * Built from the origin serving the page, so a self-hosted install hands out
 * its own script and the hosted service hands out stagepost.io's. The server
 * builds the script it answers from its own configured origin. An origin does
 * not change while a page is open, so reading it once at load is enough.
 */
export const INSTALL_COMMAND = `curl -fsSL ${window.location.origin}/install.sh | sh`

export const LOGIN_COMMAND = 'stagepost login'

/**
 * A command to paste, with a button that copies it.
 *
 * The `$` is drawn and never copied, because a prompt pasted into a terminal
 * is a command that fails. Several lines copy as several lines, each with its
 * own prompt on screen. The command scrolls sideways rather than wrapping: a
 * line break inside it is one somebody might paste.
 */
export function InstallCommand({ command = INSTALL_COMMAND, label = 'Copy' }: {
  command?: string
  label?: string
}) {
  const [state, copy] = useCopyState()
  const lines = command.split('\n')

  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 10, minWidth: 0,
        padding: '6px 6px 6px 14px', borderRadius: radius.sm,
        background: 'var(--panel-strong)', border: '1px solid var(--border-strong)',
      }}
    >
      <pre
        style={{
          // The track is hidden, not the overflow: on a narrow screen the line
          // still scrolls under a finger, and the button copies all of it.
          flex: 1, minWidth: 0, margin: 0, overflowX: 'auto', scrollbarWidth: 'none', padding: '4px 0',
          font: '400 12.5px/1.65 var(--font-machine)', color: 'var(--text)', whiteSpace: 'pre',
        }}
      >
        {lines.map((line) => (
          <div key={line}>
            <span aria-hidden="true" style={{ color: 'var(--text-faint)', userSelect: 'none' }}>$ </span>
            <code>{line}</code>
          </div>
        ))}
      </pre>
      <button
        type="button"
        onClick={() => void copy(command)}
        aria-label={`${label}: ${command}`}
        style={{
          flex: 'none', alignSelf: lines.length > 1 ? 'flex-start' : 'center',
          padding: '7px 12px', borderRadius: radius.xs, cursor: 'pointer',
          background: state === 'copied' ? 'var(--accent-wash)' : 'transparent',
          border: `1px solid ${state === 'copied' ? 'var(--accent-edge)' : 'var(--border-strong)'}`,
          color: state === 'copied' ? 'var(--accent-bright)' : 'var(--text-dim)',
          font: '600 11.5px var(--font-interface)',
        }}
      >
        {copyLabel(state, label)}
      </button>
    </div>
  )
}
